import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import StaffLayout from "./staff/StaffLayout";
import "react-datepicker/dist/react-datepicker.css";
import "./SuiteAssignmentPage.css";

const SuiteAssignmentPage = () => {
  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0); // Round down to the current hour
    return now;
  });
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const token = localStorage.getItem("jwtToken");

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        // Round selectedDateTime to the start of the hour.
        const blockStart = new Date(selectedDateTime);
        blockStart.setMinutes(0, 0, 0);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/assignments/assignments`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { time: blockStart.toISOString() },
          }
        );
        setBookings(response.data.bookings);
        setError("");
      } catch (err) {
        console.error(
          "Error fetching assignments:",
          err.response ? err.response.data : err
        );
        setError(err.response?.data?.message || "Error fetching assignments.");
      }
    };
    fetchAssignments();
  }, [selectedDateTime, token]);

  // Define the complete list of suites.
  // Both redlight and sauna suites are included.
  const allSuites = [
    { type: "massage", number: 1 },
    { type: "redlight", number: 9 },
    { type: "redlight", number: 10 },
    { type: "sauna", number: 2 },
    { type: "sauna", number: 3 },
    { type: "sauna", number: 4 },
    { type: "sauna", number: 5 },
    { type: "sauna", number: 6 },
    { type: "sauna", number: 7 },
    { type: "sauna", number: 8 },
  ];

  // Split into two columns based on odd/even suite numbers.
  const oddSuites = allSuites
    .filter((suite) => suite.number % 2 === 1)
    .sort((a, b) => b.number - a.number); // descending order

  const evenSuites = allSuites
    .filter((suite) => suite.number % 2 === 0)
    .sort((a, b) => b.number - a.number); // descending order

  // Build a mapping from suite key to booking(s).
  // For redlight suites, we accumulate bookings in an array.
  const bookingMap = {};
  bookings.forEach((booking) => {
    if (
      booking.suiteAssignment &&
      booking.suiteAssignment.type &&
      booking.suiteAssignment.number
    ) {
      const key = `${booking.suiteAssignment.type}-${booking.suiteAssignment.number}`;
      if (booking.suiteAssignment.type === "redlight") {
        if (!bookingMap[key]) {
          bookingMap[key] = [];
        }
        bookingMap[key].push(booking);
      } else {
        bookingMap[key] = booking;
      }
    }
  });

  // Sort redlight bookings chronologically for each redlight suite.
  Object.keys(bookingMap).forEach((key) => {
    if (key.startsWith("redlight-") && Array.isArray(bookingMap[key])) {
      bookingMap[key].sort(
        (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate)
      );
    }
  });

  // Helper: Format a booking's appointment time.
  const formatTime = (booking) => {
    return new Date(booking.appointmentDate).toLocaleTimeString("en-US", {
      timeZone: "America/Phoenix",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Helper: Format user name for display
  const formatUserName = (user) => {
    if (!user) return "Unknown User";
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.username) {
      return user.username;
    }
    return "Unknown User";
  };

  return (
    <StaffLayout>
      <div className="suite-assignment-page">
        <h1>Suite Assignments</h1>
        <div className="time-picker">
          <label>Select Date & Time:</label>
          <DatePicker
            selected={selectedDateTime}
            onChange={(date) => setSelectedDateTime(date)}
            showTimeSelect
            dateFormat="MMMM d, yyyy h:mm aa"
            minDate={new Date()}
          />
        </div>
        {error && <p className="error-message">{error}</p>}

        <div className="assignment-grid">
          {/* Left Column: Odd numbered suites */}
          <div className="column">
            {oddSuites.map((suite, idx) => {
              const key = `${suite.type}-${suite.number}`;
              const booking = bookingMap[key];
              return (
                <div
                  key={idx}
                  className={`suite-cell ${suite.handicap ? "handicap" : ""}`}
                >
                  <div className="suite-header">
                    {suite.type === "redlight"
                      ? `Red Light Suite #${suite.number}`
                      : suite.type === "massage"
                      ? `Massage Suite #${suite.number}`
                      : suite.handicap
                      ? "ADA Sauna #1"
                      : `Sauna Suite #${suite.number}`}
                  </div>
                  <div className="suite-content">
                    {suite.type === "redlight"
                      ? booking && Array.isArray(booking) && booking.length > 0
                        ? booking.map((b, i) => (
                            <div key={i}>
                              {formatUserName(b.user)} {formatTime(b)}
                            </div>
                          ))
                        : "Available"
                      : suite.type === "massage"
                      ? booking && booking.user
                        ? `${formatUserName(booking.user)} ${formatTime(booking)}`
                        : "Available"
                      : booking && booking.user
                      ? formatUserName(booking.user)
                      : "Available"}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Hallway */}
          <div className="hallway">Hallway</div>
          {/* Right Column: Even numbered suites */}
          <div className="column">
            {evenSuites.map((suite, idx) => {
              const key = `${suite.type}-${suite.number}`;
              const booking = bookingMap[key];
              return (
                <div key={idx} className="suite-cell">
                  <div className="suite-header">
                    {suite.type === "redlight"
                      ? `Red Light Suite #${suite.number}`
                      : suite.type === "massage"
                      ? `Massage Suite #${suite.number}`
                      : `Sauna Suite #${suite.number}`}
                  </div>
                  <div className="suite-content">
                    {suite.type === "redlight"
                      ? booking && Array.isArray(booking) && booking.length > 0
                        ? booking.map((b, i) => (
                            <div key={i}>
                              {formatUserName(b.user)} {formatTime(b)}
                            </div>
                          ))
                        : "Available"
                      : suite.type === "massage"
                      ? booking && booking.user
                        ? `${formatUserName(booking.user)} ${formatTime(booking)}`
                        : "Available"
                      : booking && booking.user
                      ? formatUserName(booking.user)
                      : "Available"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StaffLayout>
  );
};

export default SuiteAssignmentPage;
